import React, { useState } from 'react';
import { Select } from 'antd';
import CreativeCTRTable from './CreativeCTRTable';
import CreativeRingChart from './CreativeRingChart';
import SearchBar from '../../../../../../shared/designSystem/components/SearchBar';
import EntityBreakdownCard from '../../../../../../shared/designSystem/components/EntityBreakdownCard';
import { prepareCTR } from '../../../../../../../core/utils/campaigns';
import { MenuItemText } from '../../../../../../shared/globalStyling/styledText';
import { getRingColor } from '../../../../../../shared/designSystem/components/BreakdownRingChart';

const { Option } = Select;

const KEY_COLORS = {
  spent: '#1665d8',
  remaining: '#34aa44',
  budget: '#030303',
  total: '#bec0c3',
  value: '#99999',
};

const SHOW_BY_OPTIONS = [
  { value: 'impressions', text: 'Impressions' },
  { value: 'CTR', text: 'CTR Percentage' },
  { value: 'clicks', text: 'Clicks' },
];

const TAB_ITEMS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

// =============================================================================
// CreativeDetails — unified Creatives + Breakdown card.
//
// v1 shipped these as two adjacent AntD Cards inside a Row (roughly 66/33
// split). They share data, filters, and context, so reading them as one
// card matches how users actually use the pair: pick a tab/search, the
// pie and the table both react.
//
// Layout:
//   ┌ head ──────────────────────────────────────────────────┐
//   │ "Creatives" eyebrow  · search · [Show By: select]       │
//   ├─ segmented tabs (All / Active / Inactive) ──────────────┤
//   │ ┌ table (flex 2) ────────────┬ breakdown (flex 1) ────┐ │
//   │ │ rows...                    │ Breakdown eyebrow     │ │
//   │ │                            │ <CreativeRingChart/>  │ │
//   │ └────────────────────────────┴────────────────────────┘ │
//   └─────────────────────────────────────────────────────────┘
// =============================================================================

const CreativeDetails = (props) => {
  const { selectedCreativeDimension, setCreativeDimension, creativeData } = props;
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState('active');

  const parsedData = prepareCTR(creativeData);
  const colorValuePairs = parsedData.map((el, index) => {
    if (el.xAxis) {
      return { ...el, value: el.xAxis, color: getRingColor(index) };
    } else if (el.value) {
      return { ...el, color: getRingColor(index) };
    }
    return { ...el, value: `Creative ${index}`, color: getRingColor(index) };
  });

  const isCreativeActive = (creative) => creative.activity === 'active';

  const tabFilteredData = colorValuePairs.filter((creative) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return isCreativeActive(creative);
    if (activeTab === 'inactive') return !isCreativeActive(creative);
    return true;
  });

  const filteredData = tabFilteredData.filter((item) =>
    String(item.value ?? '')
      .toLowerCase()
      .includes(searchValue.toLowerCase())
  );

  return (
    <EntityBreakdownCard
      title="Creatives"
      search={
        <SearchBar
          placeholder="Search creatives..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="campaign-overview__creatives-search"
        />
      }
      tabs={
        <div
          className="campaign-overview__creatives-tabs segmented-tabs"
          role="tablist"
          aria-label="Creative activity filter"
        >
          {TAB_ITEMS.map((t) => (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={activeTab === t.key}
              onClick={() => setActiveTab(t.key)}
              className={
                'segmented-tabs__btn' +
                (activeTab === t.key ? ' segmented-tabs__btn--active' : '')
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      }
      table={<CreativeCTRTable creativeData={filteredData} />}
      breakdownControl={
        <Select
          defaultValue={SHOW_BY_OPTIONS[0].value}
          onChange={(value) => setCreativeDimension(value)}
          getPopupContainer={(trigger) => trigger.parentNode}
          popupClassName="mjl-ds"
          className="campaign-overview__creatives-showby"
          size="small"
        >
          {SHOW_BY_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              <MenuItemText text={option.text} />
            </Option>
          ))}
        </Select>
      }
      breakdownChart={
        <CreativeRingChart
          data={filteredData}
          keyColors={KEY_COLORS}
          height={480}
          selectedCreativeDimension={selectedCreativeDimension}
        />
      }
    />
  );
};

export default CreativeDetails;
